// modules
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "antd";

// service calls

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants and types
import { MERGE_CR_ACCOUNTS as Constants } from "constants/global";
import { FormInstance } from "antd/es/form/Form";
import { mergeCRAccounts } from "services/api/crAccount";

type MergeCRAccountsPropTypes = {
	proceedToMergeAccounts: () => void;
	loading: boolean;
	uidToCancel: string;
	uidToRetain: string;
	emailToRetain: string;
	form: FormInstance;
	handleUidToCancel: (e: { target: { id: any; value: any } }) => void;
	handleUidToRetain: (e: { target: { id: any; value: any } }) => void;
	handleEmailToRetain: (e: { target: { id: any; value: any } }) => void;
};

export function useMergeCRAccountsPage(): MergeCRAccountsPropTypes {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState<boolean>(false);
	const [uidToCancel, setUidToCancel] = useState<string>("");
	const [uidToRetain, setUidToRetain] = useState<string>("");
	const [emailToRetain, setEmailToRetain] = useState<string>("");

	const onSuccess = useCallback(() => {
		navigate(0);
	}, [navigate]);

	const onFailure = useCallback(
		(error: any) => {
			setLoading(false);
			CountDownModal({
				closable: true,
				timer: 10,
				errorIcon: true,
				title: Constants.SERVER_ERR,
				subtitle: error?.message,
				onTimeComplete: () => {
					return false;
				}
			});
		},
		[loading]
	);

	const handleUidToCancel = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setUidToCancel(e?.target?.value);
		},
		[uidToCancel]
	);

	const handleUidToRetain = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setUidToRetain(e?.target?.value);
		},
		[uidToRetain]
	);

	const handleEmailToRetain = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setEmailToRetain(e?.target?.value);
		},
		[emailToRetain]
	);

	const proceedToMergeAccounts = useCallback(async () => {
		if (uidToCancel && ((uidToRetain && emailToRetain) || (!uidToRetain && !emailToRetain))) {
			setLoading(true);
			await mergeCRAccounts({ uid_to_cancel: uidToCancel, uid_to_retain: uidToRetain, email_to_retain: emailToRetain })
				.then((response) => {
					CountDownModal({
						timer: 3,
						errorIcon: false,
						title: Constants.SUCCESS,
						subtitle: response?.data?.message,
						onTimeComplete: onSuccess
					});
				})
				.catch((error) => {
					const errors = error?.response?.data?.errors;
					if (Array.isArray(errors) && errors.length > 0) {
						onFailure(errors[0]);
					} else {
						onFailure({ message: Constants.SERVER_ERR });
					}
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [uidToCancel, uidToRetain, emailToRetain, loading, setUidToCancel, setUidToRetain, setEmailToRetain]);

	return {
		proceedToMergeAccounts,
		loading,
		uidToCancel,
		uidToRetain,
		emailToRetain,
		form,
		handleUidToCancel,
		handleUidToRetain,
		handleEmailToRetain
	};
}
